import React from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { formatDateTime } from '../Utils/GlobalUtils';
import { useTimeModal } from '../Components/TimeModal/TimeModalContext';
import { useNotifications } from '../Components/Context/NotificationsCountContext';

import HeadlinePanel from '../Components/HeadlinePanel';

import Loading from '../Components/Loading';
import LoadingLines from '../Components/LoadingLines';
import WarningPanel from '../Components/WarningPanel';

import styles from '../styles/Inbox.module.css';

import IconContainer from '../Components/IconContainer';
import { ReactComponent as TrashIcon } from '../icons/trash.svg';

export default function Inbox() {
  const { openTimeModal } = useTimeModal();
  const location = useLocation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = React.useState([]);
  const [warning, setWarning] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [reload, setReload] = React.useState(true);

  const [limitForView, setLimitForView] = React.useState(50);

  React.useEffect(() => {
    const token = localStorage.getItem('token');

    setLoading(true);
    
    axios.get(`/api/get-user-inbox?limitForView=${limitForView}`, {
      headers: {
          Authorization: `Bearer ${token}`,
      }
    }).then(response => {
        if (response.data.length === 0) {
            setWarning('Уведомлений нет');
            setNotifications([]);
        } else {
            setWarning('');
            setNotifications(response.data);
        }
    })
    .catch(error => {
        setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
    })
    .finally(() => {
      setReload(false);
      setLoading(false);
    })
  }, [reload]);

  const { decrementCount, markAllAsRead } = useNotifications();

  const changeNotificationStatus = (id, status) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.put(`/api/change-notification-status`, {
      id: id,
      status: status
    }, {
      headers: {
          Authorization: `Bearer ${token}`,
      }
    }).then(() => {
      if (id === 'all') {
        markAllAsRead();
      } else {
        decrementCount(1);
      }

      setReload(true);
    }).catch(() => {
      openTimeModal('Не удалось скрыть уведомление. Попробуйте перезагрузить страницу', 'error')
    }).finally(() => {
      setLoading(false);
    });
  };


  return (
    <>
      <div className='mainContainer'>
        <HeadlinePanel label= 'Уведомления' type='' />
        <div className='containerInner'>
          <div className='panel'>
            <span style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px'}}>
              <p>
                {loading ? 'Загрузка...'
                : notifications.some(n => n.status === '0') ? `Непрочитанных уведомлений: ${notifications.filter(n => n.status === '0').length}` : 'Все уведомления прочитаны'}
              </p>
              {notifications.some(n => n.status === '0') && (
                <button type='button' className='button miniButton' style={{width: 'fit-content'}} onClick={() => {changeNotificationStatus('all', '1')}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256"><path d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"></path></svg>
                  Отметить все как прочитанные
                </button>
              )}
            </span>
          </div>
          <div className='pageWrapper'>
            <div className='tableContainer withoutPaddings'>
              {(!notifications && loading)? <LoadingLines />
              : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить список уведомлений' buttonAction={() => setReload(true)} />
              : notifications.map((notif) => 
                  <motion.div
                    className='tableRow nonActive' 
                    key={notif.id}
                    initial={{ scale: '97%', opacity: 0 }}
                    animate={{ scale: '100%', opacity: 1 }}
                    exit={{ scale: '97%', opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ gridTemplateColumns: '10fr 54fr 15fr 20fr 1fr' }}
                  >
                    <div>
                      <p
                        className='monoTime'
                      >
                        {formatDateTime(notif.created_at)}
                      </p>
                    </div>
                    <div>
                      {notif.content}
                      {notif.case_id && 
                        <Link 
                          className='aWithLink' 
                          to={`/case/${notif.case_id}`}
                          onClick={() => {changeNotificationStatus(notif.id, '1')}}
                        >
                          Открыть карточку дела
                        </Link>}
                      {notif.type_of_notification=='Отправлено обращение в поддержку' && 
                        <Link 
                          className='aWithLink' 
                          to={`/view-feedback`}
                          onClick={() => {changeNotificationStatus(notif.id, '1')}}
                        >
                          Перейти
                        </Link>
                      }
                    </div>
                    <div>
                      {notif.status == '0' && <p className='textPill blue'>Не прочитано</p>}
                    </div>
                    <div>
                      {notif.status == '0' && 
                      <button 
                        type='button' 
                        className='button miniButton' 
                        onClick={() => {changeNotificationStatus(notif.id, '1')}}
                        disabled={loading}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256"><path d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"></path></svg>
                        Отметить прочитанным
                      </button>}
                    </div>
                    <div>
                      <IconContainer Icon={TrashIcon} onClick={() => {changeNotificationStatus(notif.id, '2')}} size = '17' color = 'red' className = '' />
                    </div>
                  </motion.div>
              )}
              {!warning && notifications.length > 0 && (notifications.length < notifications[0].total_count) && (
                  <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                      <div></div>
                      <div>
                          <button
                              type='button'
                              className='button'
                              style={{ width: 'fit-content' }}
                              onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                              disabled={loading}
                          >
                              {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                          </button>
                      </div>
                      <div></div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
